import React from 'react';
import {
  ActionIcon,
  Box,
  Group,
  HoverCard,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconArrowDownRight,
  IconArrowUpRight,
  IconChartBar,
  IconMinus,
} from '@tabler/icons-react';
import {
  formatVarianceDays,
  type BaselineVarianceSummary,
} from '../../../gantt/baselineCompareUtils';

export interface GanttBaselineVarianceHintProps {
  baselineLabel: string;
  summary: BaselineVarianceSummary;
}

const VARIANCE_HINT_STYLES = `
@keyframes fbt-baseline-variance-shine {
  0% {
    transform: translateX(-140%) rotate(25deg);
  }
  100% {
    transform: translateX(140%) rotate(25deg);
  }
}
@keyframes fbt-baseline-variance-enter {
  from {
    opacity: 0;
    transform: translateX(-12px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}
.fbt-baseline-variance-hint-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  overflow: hidden;
  isolation: isolate;
  animation: fbt-baseline-variance-enter 0.65s cubic-bezier(0.22, 1, 0.36, 1) .5s both;
}
.fbt-baseline-variance-hint-shine {
  position: absolute;
  inset: -20% -40%;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(
    105deg,
    transparent 36%,
    color-mix(in srgb, #ffffff 0%, transparent) 42%,
    color-mix(in srgb, #ffffff 70%, transparent) 50%,
    color-mix(in srgb, #ffffff 0%, transparent) 58%,
    transparent 64%
  );
  animation: fbt-baseline-variance-shine 2.4s ease-in-out infinite;
  mix-blend-mode: soft-light;
}
.fbt-baseline-variance-hint-wrap::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 1;
  box-shadow: inset 0 1px 0 color-mix(in srgb, #ffffff 35%, transparent);
  background: radial-gradient(
    120% 80% at 30% 20%,
    color-mix(in srgb, #ffffff 28%, transparent) 0%,
    transparent 55%
  );
}
.fbt-baseline-variance-hint-wrap:hover .fbt-baseline-variance-hint-shine,
.fbt-baseline-variance-hint-wrap:focus-within .fbt-baseline-variance-hint-shine {
  animation-duration: 1.2s;
}
@media (prefers-reduced-motion: reduce) {
  .fbt-baseline-variance-hint-wrap {
    animation: none;
  }
  .fbt-baseline-variance-hint-shine {
    animation: none;
    opacity: 0;
  }
}
`;

function MetricTile({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Box
      style={{
        borderRadius: 10,
        padding: '10px 8px',
        background: `color-mix(in srgb, ${color} 10%, var(--mantine-color-body))`,
        border: `1px solid color-mix(in srgb, ${color} 18%, var(--mantine-color-default-border))`,
        textAlign: 'center',
        minWidth: 0,
      }}
    >
      <Text
        fw={700}
        style={{
          fontSize: 20,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Text>
      <Text size="xs" c="dimmed" mt={4} style={{ lineHeight: 1.2 }}>
        {label}
      </Text>
    </Box>
  );
}

function projectFinishCopy(days: number | null): {
  title: string;
  detail: string;
  color: string;
  Icon: typeof IconMinus;
} {
  if (days == null) {
    return {
      title: 'Finish unknown',
      detail: 'Need finish dates on both sides',
      color: 'var(--mantine-color-dimmed)',
      Icon: IconMinus,
    };
  }
  if (days === 0) {
    return {
      title: 'On plan',
      detail: 'Latest finish matches baseline',
      color: 'var(--mantine-color-teal-6)',
      Icon: IconMinus,
    };
  }
  if (days > 0) {
    return {
      title: `+${days}d later`,
      detail: 'Latest finish vs baseline',
      color: 'var(--mantine-color-orange-6)',
      Icon: IconArrowUpRight,
    };
  }
  return {
    title: `${days}d earlier`,
    detail: 'Latest finish vs baseline',
    color: 'var(--mantine-color-teal-6)',
    Icon: IconArrowDownRight,
  };
}

/**
 * Animated analytics affordance next to the baseline Compare dropdown.
 * Hover (or focus) opens a compact variance popover.
 */
export function GanttBaselineVarianceHint({
  baselineLabel,
  summary,
}: GanttBaselineVarianceHintProps) {
  const hasSlip = summary.laterCount > 0;
  const iconColor = hasSlip ? 'orange' : 'brand';
  const finish = projectFinishCopy(summary.projectFinishDays);
  const FinishIcon = finish.Icon;
  const comparedLabel =
    summary.comparedCount > 0
      ? `${summary.comparedCount} leaf tasks compared`
      : 'No comparable leaf finish dates';

  return (
    <>
      <style>{VARIANCE_HINT_STYLES}</style>
      <HoverCard
        width={320}
        shadow="lg"
        radius="lg"
        withArrow
        openDelay={120}
        closeDelay={120}
        position="bottom-end"
        withinPortal
        styles={{
          dropdown: {
            padding: 0,
            overflow: 'hidden',
            border: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-body)',
          },
        }}
      >
        <HoverCard.Target>
          <span className="fbt-baseline-variance-hint-wrap">
            <ActionIcon
              variant="light"
              color={iconColor}
              size={28}
              radius="xl"
              aria-label={`Baseline variance vs ${baselineLabel}`}
            >
              <IconChartBar size={15} />
            </ActionIcon>
            <span className="fbt-baseline-variance-hint-shine" aria-hidden />
          </span>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Box
            px="md"
            pt="md"
            pb="sm"
            style={{
              background:
                'linear-gradient(180deg, color-mix(in srgb, var(--mantine-color-brand-6) 8%, var(--mantine-color-body)) 0%, var(--mantine-color-body) 100%)',
              borderBottom: '1px solid var(--mantine-color-default-border)',
            }}
          >
            <Text
              size="xs"
              tt="uppercase"
              fw={600}
              c="dimmed"
              style={{ letterSpacing: '0.06em' }}
            >
              Baseline variance
            </Text>
            <Text fw={700} mt={4} style={{ fontSize: 15, lineHeight: 1.25, letterSpacing: '-0.01em' }}>
              Live vs {baselineLabel}
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {comparedLabel}
              {summary.missingFinishCount > 0
                ? ` · ${summary.missingFinishCount} skipped`
                : ''}
            </Text>
          </Box>

          <Stack gap="md" p="md">
            <SimpleGrid cols={3} spacing={8}>
              <MetricTile
                label="On track"
                value={summary.onTrackCount}
                color="var(--mantine-color-gray-6)"
              />
              <MetricTile
                label="Later"
                value={summary.laterCount}
                color="var(--mantine-color-orange-6)"
              />
              <MetricTile
                label="Earlier"
                value={summary.earlierCount}
                color="var(--mantine-color-teal-6)"
              />
            </SimpleGrid>

            <Box
              style={{
                borderRadius: 12,
                padding: '12px 14px',
                border: '1px solid var(--mantine-color-default-border)',
                background: 'color-mix(in srgb, var(--mantine-color-default-border) 35%, transparent)',
              }}
            >
              <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                <div style={{ minWidth: 0 }}>
                  <Text size="xs" c="dimmed" style={{ lineHeight: 1.2 }}>
                    Project finish
                  </Text>
                  <Text
                    fw={700}
                    mt={2}
                    style={{
                      fontSize: 16,
                      lineHeight: 1.2,
                      letterSpacing: '-0.02em',
                      color: finish.color,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {finish.title}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    {finish.detail}
                  </Text>
                </div>
                <ThemeIcon
                  size={36}
                  radius="xl"
                  variant="light"
                  color={
                    summary.projectFinishDays == null || summary.projectFinishDays === 0
                      ? 'gray'
                      : summary.projectFinishDays > 0
                        ? 'orange'
                        : 'teal'
                  }
                >
                  <FinishIcon size={18} />
                </ThemeIcon>
              </Group>
            </Box>

            <Box
              style={{
                borderRadius: 12,
                padding: '10px 12px',
                border: '1px dashed var(--mantine-color-default-border)',
              }}
            >
              <Text size="xs" c="dimmed" mb={4}>
                Biggest slip
              </Text>
              {summary.biggestSlip ? (
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                  <Text size="sm" fw={600} lineClamp={2} style={{ lineHeight: 1.3, minWidth: 0 }}>
                    {summary.biggestSlip.taskName}
                  </Text>
                  <Text
                    size="sm"
                    fw={700}
                    c="orange"
                    style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatVarianceDays(summary.biggestSlip.finishDays)}
                  </Text>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  None — no late leaf tasks
                </Text>
              )}
            </Box>

            <Text size="xs" c="dimmed" style={{ lineHeight: 1.35 }}>
              On track = same finish day. Later / earlier = after / before the approved
              baseline.
            </Text>
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>
    </>
  );
}

/** @deprecated Use GanttBaselineVarianceHint */
export const GanttBaselineVarianceStrip = GanttBaselineVarianceHint;
